import {CalculatorProps} from 'components/calculator/models';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import React, {useEffect} from 'react';
import {getConfig} from 'units/calculator/api';
import {compose} from 'recompose';
import withData from 'components/calculator/withData';
import {useForm, useFormState} from 'react-final-form';
import {useFieldArray} from 'react-final-form-arrays';
import CalculatorForm from 'components/calculator/CalculatorForm/CalculatorForm';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import ClosableSection from 'components/calculator/ClosableSection/ClosableSection';
import AddButton from 'components/Buttons/AddButton/AddButton';
import GreenplumFormItem, {
    GreenplumFormItemValues,
} from 'components/calculator/GreenplumForm/GreenplumFormItem/GreenplumFormItem';
import {
    GreenplumPreparedConfig,
    normalizeConfig,
    prepareConfig,
} from 'components/calculator/GreenplumForm/utils';
import {DiskSize, HostType, RawGreenplumConfig} from 'components/calculator/GreenplumForm/types';
import {findClosest, getValueInRange} from 'utils';

export interface GreenplumFormValues {
    items: GreenplumFormItemValues[];
}

interface GreenplumFormOwnProps extends CalculatorProps {}

interface GreenplumFormDataProps {
    data: {
        master: GreenplumPreparedConfig;
        segment: GreenplumPreparedConfig;
    };
}

type GreenplumFormProps = GreenplumFormDataProps & GreenplumFormOwnProps & WithTranslationProps;

const GreenplumForm: React.FC<GreenplumFormProps> = (props) => {
    const {title, data: hostData, i18nK} = props;

    const {initialize} = useForm<GreenplumFormValues>();
    const {values, initialValues} = useFormState<GreenplumFormValues>();
    const {fields} = useFieldArray<GreenplumFormItemValues>('items');

    const createItemDefaultData = React.useCallback(() => {
        const getProps = (data: GreenplumPreparedConfig) => {
            const {
                defaultResources: {resourcePresetId, generation, diskTypeId, diskSize},
                resourcePresets,
                presetZoneDiskTypes,
            } = data;

            let preset = resourcePresets.find((_preset) => _preset.presetId === resourcePresetId);
            if (!preset && resourcePresets.length) {
                preset =
                    resourcePresets.find((_preset) => _preset.generation === generation) ||
                    resourcePresets[0];
            }
            if (!preset) {
                throw new Error('Preset is not found');
            }

            const diskTypes = presetZoneDiskTypes.get(preset) || [];
            let presetDisk = diskTypes.find((disk) => disk.diskTypeId === diskTypeId);
            if (!presetDisk && diskTypes.length) {
                presetDisk = diskTypes[0];
            }
            if (!presetDisk) {
                throw new Error('Preset disk is not found');
            }

            let size;
            if (presetDisk.DiskSize === DiskSize.DISK_SIZES) {
                size = findClosest(presetDisk.diskSizes.sizes, diskSize) || 0;
            } else {
                size = getValueInRange(presetDisk.diskSizeRange, diskSize);
            }

            return {
                presetId: preset.presetId,
                platform: preset.generation,
                type: preset.type,
                hosts: presetDisk.minHosts,
                disk: presetDisk.diskTypeId,
                size,
            };
        };

        const items: GreenplumFormItemValues = {
            master: getProps(hostData.master),
            segment: getProps(hostData.segment),
            ip: false,
            traffic: 0,
        };

        return items;
    }, [hostData]);

    useEffect(() => {
        initialize({
            items: [createItemDefaultData()],
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddClusterClick = React.useCallback(() => {
        fields.push(createItemDefaultData());
    }, [createItemDefaultData, fields]);

    if (!initialValues) {
        return null;
    }

    return (
        <CalculatorForm title={title}>
            <CalculatorFormLayout>
                {fields.map((name, index) => (
                    <CalculatorFormLayout.Section key={index}>
                        <ClosableSection
                            showClose={Number(fields.length) > 1}
                            onClose={() => fields.remove(index)}
                        >
                            <GreenplumFormItem
                                data={hostData}
                                values={values.items[index]}
                                index={index}
                                name={name}
                            />
                        </ClosableSection>
                    </CalculatorFormLayout.Section>
                ))}
                <CalculatorFormLayout.Section>
                    <AddButton onClick={handleAddClusterClick}>
                        {i18nK('mdb-button_add_cluster')}
                    </AddButton>
                </CalculatorFormLayout.Section>
            </CalculatorFormLayout>
        </CalculatorForm>
    );
};

const formDataLoader = async (props: GreenplumFormProps) => {
    const {locale} = props;
    const config = (await getConfig(locale, 'greenplum')) as RawGreenplumConfig;
    const normConfig = normalizeConfig(config);
    return {
        master: prepareConfig(normConfig, HostType.MASTER),
        segment: prepareConfig(normConfig, HostType.SEGMENT),
    };
};

export default compose<GreenplumFormProps, GreenplumFormOwnProps>(
    withData({loader: formDataLoader}),
    withTranslation('calculator'),
)(GreenplumForm);
